import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add,
  Delete,
  Edit,
  FileCopy,
  Refresh,
  Visibility
} from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  panel: {
    padding: theme.spacing(2),
    borderRadius: 8
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    marginBottom: theme.spacing(1)
  },
  linkText: {
    maxWidth: 320,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  empty: {
    padding: theme.spacing(5),
    textAlign: "center",
    color: "#667"
  }
}));

const CAMPOS_PADRAO = [
  { key: "name", label: "Nome", type: "text", required: true },
  { key: "phone", label: "WhatsApp", type: "phone", required: true },
  { key: "email", label: "E-mail", type: "email", required: false },
  { key: "course", label: "Curso de interesse", type: "text", required: false },
  { key: "city", label: "Cidade", type: "text", required: false },
  { key: "observation", label: "Observação", type: "textarea", required: false }
];

const estadoInicial = {
  title: "",
  description: "",
  originLabel: "",
  action: "contact",
  sendToCrm: false,
  isActive: true,
  fields: CAMPOS_PADRAO.slice(0, 2)
};

const normalizarCampoCustomizado = label => {
  const base = String(label || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return base || `campo_${Date.now()}`;
};

const LeadForms = () => {
  const classes = useStyles();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(estadoInicial);
  const [customLabel, setCustomLabel] = useState("");
  const [submissionsOpen, setSubmissionsOpen] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);

  const formUrl = token => `${window.location.origin}/form/${token}`;

  const loadForms = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lead-forms");
      setForms(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  const selectedKeys = useMemo(
    () => new Set((formData.fields || []).map(field => field.key)),
    [formData.fields]
  );

  const openNew = () => {
    setEditing(null);
    setFormData(estadoInicial);
    setModalOpen(true);
  };

  const openEdit = form => {
    setEditing(form);
    setFormData({
      title: form.title || "",
      description: form.description || "",
      originLabel: form.originLabel || "",
      action: form.action || "contact",
      sendToCrm: Boolean(form.sendToCrm),
      isActive: form.isActive !== false,
      fields: Array.isArray(form.fields) ? form.fields : []
    });
    setModalOpen(true);
  };

  const toggleCampoPadrao = campo => {
    setFormData(prev => {
      const exists = prev.fields.some(field => field.key === campo.key);
      return {
        ...prev,
        fields: exists
          ? prev.fields.filter(field => field.key !== campo.key)
          : [...prev.fields, campo]
      };
    });
  };

  const toggleObrigatorio = key => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(field =>
        field.key === key ? { ...field, required: !field.required } : field
      )
    }));
  };

  const addCustomField = () => {
    const label = customLabel.trim();
    if (!label) return;

    const key = normalizarCampoCustomizado(label);
    setFormData(prev => {
      if (prev.fields.some(field => field.key === key)) return prev;
      return {
        ...prev,
        fields: [...prev.fields, { key, label, type: "text", required: false }]
      };
    });
    setCustomLabel("");
  };

  const removeField = key => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.key !== key)
    }));
  };

  const saveForm = async () => {
    try {
      const payload = {
        ...formData,
        originLabel: formData.originLabel || formData.title
      };

      if (editing) {
        await api.put(`/lead-forms/${editing.id}`, payload);
      } else {
        await api.post("/lead-forms", payload);
      }

      toast.success("Formulário salvo com sucesso.");
      setModalOpen(false);
      loadForms();
    } catch (err) {
      toastError(err);
    }
  };

  const deleteForm = async form => {
    if (!window.confirm(`Excluir o formulário "${form.title}"?`)) return;
    try {
      await api.delete(`/lead-forms/${form.id}`);
      toast.success("Formulário excluído.");
      loadForms();
    } catch (err) {
      toastError(err);
    }
  };

  const copyLink = async token => {
    try {
      await navigator.clipboard.writeText(formUrl(token));
      toast.success("Link copiado.");
    } catch {
      toast.info(formUrl(token));
    }
  };

  const openSubmissions = async form => {
    setSelectedForm(form);
    setSubmissionsOpen(true);
    try {
      const { data } = await api.get(`/lead-forms/${form.id}/submissions`);
      setSubmissions(data || []);
    } catch (err) {
      toastError(err);
      setSubmissions([]);
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4" color="primary">Formulários de cadastro</Typography>
        <Box>
          <Button startIcon={<Refresh />} onClick={loadForms} disabled={loading}>
            Atualizar
          </Button>
          <Button color="primary" variant="contained" startIcon={<Add />} onClick={openNew}>
            Novo formulário
          </Button>
        </Box>
      </Box>

      <Paper className={classes.panel}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Finalidade</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Link</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.map(form => (
              <TableRow key={form.id}>
                <TableCell>{form.title}</TableCell>
                <TableCell>
                  {form.action === "ticket"
                    ? "Criar contato e atendimento"
                    : form.action === "contact_ticket_origin"
                      ? "Contato, atendimento e origem"
                      : "Somente contato"}
                </TableCell>
                <TableCell>{form.originLabel}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Typography className={classes.linkText}>{formUrl(form.token)}</Typography>
                    <IconButton size="small" onClick={() => copyLink(form.token)}>
                      <FileCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>{form.isActive ? "Ativo" : "Inativo"}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openSubmissions(form)} title="Ver respostas">
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => openEdit(form)} title="Editar">
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => deleteForm(form)} title="Excluir">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!forms.length && (
              <TableRow>
                <TableCell colSpan={6} className={classes.empty}>
                  Nenhum formulário criado ainda.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Editar formulário" : "Novo formulário"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome do formulário"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Origem registrada"
                value={formData.originLabel}
                onChange={e => setFormData({ ...formData, originLabel: e.target.value })}
                placeholder="Formulário: Bolsa Administração"
                fullWidth
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição exibida no link"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Finalidade</InputLabel>
                <Select
                  value={formData.action}
                  onChange={e => setFormData({ ...formData, action: e.target.value })}
                  label="Finalidade"
                >
                  <MenuItem value="contact">Criar ou atualizar contato</MenuItem>
                  <MenuItem value="ticket">Criar contato e abrir atendimento</MenuItem>
                  <MenuItem value="contact_ticket_origin">Contato, atendimento e origem</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" height="100%">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Link ativo"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sendToCrm}
                      onChange={e => setFormData({ ...formData, sendToCrm: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Deixar disponível para CRM"
                />
              </Box>
            </Grid>
          </Grid>

          <Box mt={2}>
            <Typography variant="h6">Campos do formulário</Typography>
            <Typography variant="body2" color="textSecondary">
              Selecione os campos que este link deve pedir. O usuário decide campo por campo.
            </Typography>
            <Grid container spacing={1}>
              {CAMPOS_PADRAO.map(campo => (
                <Grid item xs={12} md={4} key={campo.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedKeys.has(campo.key)}
                        onChange={() => toggleCampoPadrao(campo)}
                        color="primary"
                      />
                    }
                    label={campo.label}
                  />
                </Grid>
              ))}
            </Grid>

            <Box display="flex" alignItems="center" mt={1} mb={2}>
              <TextField
                label="Campo personalizado"
                value={customLabel}
                onChange={e => setCustomLabel(e.target.value)}
                variant="outlined"
                margin="dense"
                fullWidth
              />
              <Button onClick={addCustomField} color="primary" variant="outlined" style={{ marginLeft: 8 }}>
                Adicionar
              </Button>
            </Box>

            {(formData.fields || []).map(field => (
              <Box className={classes.fieldRow} key={field.key}>
                <Typography style={{ flex: 1 }}>{field.label}</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(field.required)}
                      onChange={() => toggleObrigatorio(field.key)}
                      color="primary"
                    />
                  }
                  label="Obrigatório"
                />
                <IconButton size="small" onClick={() => removeField(field.key)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={saveForm}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={submissionsOpen} onClose={() => setSubmissionsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Respostas: {selectedForm?.title}</DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Dados</TableCell>
                <TableCell>Atendimento</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map(row => (
                <TableRow key={row.id}>
                  <TableCell>{new Date(row.createdAt).toLocaleString("pt-BR")}</TableCell>
                  <TableCell>{row.contact?.name || "-"}</TableCell>
                  <TableCell>
                    {Object.entries(row.values || {}).map(([key, value]) => (
                      <Typography key={key} variant="body2">
                        <strong>{key}:</strong> {String(value || "-")}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell>{row.ticket ? `#${row.ticket.id}` : "-"}</TableCell>
                </TableRow>
              ))}
              {!submissions.length && (
                <TableRow>
                  <TableCell colSpan={4} className={classes.empty}>
                    Nenhuma resposta recebida ainda.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmissionsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadForms;
